import { Ability } from '@casl/ability'
import { ref, computed } from 'vue'

export const actions = {
  VIEW: 'view',
  LIST: 'list',
  CREATE: 'create',
  EDIT: 'edit',
  DELETE: 'delete',
  EXPORT: 'export',
  MANAGE: 'manage',
  IMPERSONATE: 'impersonate',
  VERIFY_BANK: 'verify',
}

export const subjects = {
  DASHBOARD: 'DASHBOARD',
  PROMOTIONS: 'PROMOTIONS',
  BUDGET: 'BUDGET',
  BUDGET_QUOTE: 'BUDGET_QUOTE',
  QUOTES: 'QUOTES',
  FILE: 'FILE',
  MESSAGE: 'MESSAGE',
  PRODUCT_ITEMS: 'PRODUCT_ITEMS',
  COMPANIES: 'COMPANIES',
  USERS: 'USERS',
  USER_INVITATION: 'USER_INVITATION',
  CONTRACTS: 'CONTRACTS',
  SIMULATIONS: 'SIMULATIONS',

  EXPORT_REPORTS: 'EXPORT_REPORTS',
  PROMOTION_CONFIG: 'PROMOTION_CONFIG',
}

export const ability = new Ability()

const currentPermissions = ref(null)
const currentRole = ref(null)
const promotionRoles = ref({})
const activePromotionId = ref(null)

// Roles que pueden usar permisos por defecto cuando no tienen permisos específicos de promoción
const DEFAULT_PERMISSION_ROLES = ['developer', 'admin', 'architect']

export const useRolePermissions = () => {
  const activeRole = computed(() => {
    if (activePromotionId.value && promotionRoles.value[activePromotionId.value]) {
      return promotionRoles.value[activePromotionId.value].role
    }
    return currentRole.value
  })

  const activePermissions = computed(() => {
    if (activePromotionId.value && promotionRoles.value[activePromotionId.value]) {
      return promotionRoles.value[activePromotionId.value].permissions
    }
    return currentPermissions.value
  })

  // Indica si el usuario tiene permisos explícitos para la promoción activa
  const hasPromotionPermissions = computed(() => {
    return activePromotionId.value && promotionRoles.value[activePromotionId.value]
  })

  // Indica si el usuario puede usar permisos por defecto (solo developer, admin, architect)
  const canUseDefaultPermissions = computed(() => {
    return DEFAULT_PERMISSION_ROLES.includes(currentRole.value)
  })

  const setPermissions = (permissionsData) => {
    currentRole.value = permissionsData.role
    currentPermissions.value = permissionsData.permissions
    const rolesArray = permissionsData.promotionRoles || []
    promotionRoles.value = Array.isArray(rolesArray)
      ? rolesArray.reduce((acc, entry) => { acc[entry.promotionId] = entry; return acc }, {})
      : rolesArray
    _updateAbility(permissionsData.permissions)
  }

  const setActivePromotion = (promotionId) => {
    activePromotionId.value = promotionId

    if (promotionId && promotionRoles.value[promotionId]) {
      // Tiene permisos específicos para esta promoción
      _updateAbility(promotionRoles.value[promotionId].permissions)
    } else if (promotionId && !DEFAULT_PERMISSION_ROLES.includes(currentRole.value)) {
      // No tiene permisos específicos y NO es developer/admin/architect
      // Denegar acceso completo a la promoción
      ability.update([])
    } else if (currentPermissions.value) {
      // Es developer/admin/architect o no hay promoción activa
      // Usar permisos por defecto
      _updateAbility(currentPermissions.value)
    }
  }

  const clearPermissions = () => {
    currentRole.value = null
    currentPermissions.value = null
    promotionRoles.value = {}
    activePromotionId.value = null
    ability.update([])
  }

  const _updateAbility = (permissions) => {
    if (!permissions) {
      ability.update([])
      return
    }

    const rules = []

    // Soportar formato array: [{subject, actions}] o [{subject, granted}]
    if (Array.isArray(permissions)) {
      for (const perm of permissions) {
        const subject = perm.subject

        if (perm.granted === true) {
          // Permiso booleano (acceso completo)
          rules.push({ action: 'view', subject })
        } else if (Array.isArray(perm.actions) && perm.actions.length > 0) {
          // Expandir cada acción en una regla individual
          for (const action of perm.actions) {
            rules.push({ action, subject })
          }
        }
      }
    } else {
      // Formato legacy (objeto con claves) - por compatibilidad
      for (const [subject, actionsValue] of Object.entries(permissions)) {
        const normalizedSubject = subject.toUpperCase()

        if (actionsValue === true) {
          rules.push({ action: 'view', subject: normalizedSubject })
        } else if (Array.isArray(actionsValue) && actionsValue.length > 0) {
          for (const action of actionsValue) {
            rules.push({ action, subject: normalizedSubject })
          }
        }
      }
    }

    ability.update(rules)
  }

  const can = (action, subject) => {
    return ability.can(action, subject)
  }

  const cannot = (action, subject) => {
    return ability.cannot(action, subject)
  }

  const canAny = (actionList, subject) => {
    return actionList.some(action => ability.can(action, subject))
  }

  const canAll = (actionList, subject) => {
    return actionList.every(action => ability.can(action, subject))
  }

  const getRolePermissions = (promotionId) => {
    if (promotionId && promotionRoles.value[promotionId]) {
      return promotionRoles.value[promotionId].permissions
    }
    return currentPermissions.value
  }

  return {
    ability,
    actions,
    subjects,
    can,
    cannot,
    canAny,
    canAll,
    setPermissions,
    setActivePromotion,
    clearPermissions,
    activeRole,
    activePermissions,
    currentRole,
    currentPermissions,
    promotionRoles,
    activePromotionId,
    getRolePermissions,
    hasPromotionPermissions,
    canUseDefaultPermissions,
  }
}
